import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import api from '../services/api'
import { Plus, Edit, Trash2, Loader2, Search, X, Check, Eye, Users, Image as ImageIcon, Briefcase, Upload, Info } from 'lucide-react'
import { useState } from 'react'

const AdminExperts = () => {
  const queryClient = useQueryClient()
  const [searchTerm, setSearchTerm] = useState('')
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isViewModalOpen, setIsViewModalOpen] = useState(false)
  const [editingExpert, setEditingExpert] = useState(null)
  const [viewingExpert, setViewingExpert] = useState(null)
  const [isUploading, setIsUploading] = useState(false)
  const [expertToDelete, setExpertToDelete] = useState(null)

  const { data, isLoading } = useQuery({
    queryKey: ['admin-experts'],
    queryFn: async () => {
      const res = await api.get('/experts/')
      return res.data
    }
  })

  const saveMutation = useMutation({
    mutationFn: (expert) => {
      const payload = {
        ...expert,
        domaines: Array.isArray(expert.domaines) ? expert.domaines : (expert.domaines?.split(',').map(s => s.trim()).filter(s => s) || []),
      }

      if (expert.id) {
        return api.put(`/experts/${expert.id}`, payload)
      } else {
        return api.post('/experts/', payload)
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['admin-experts'])
      setIsModalOpen(false)
      setEditingExpert(null)
    }
  })

  const deleteMutation = useMutation({
    mutationFn: (id) => api.delete(`/experts/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries(['admin-experts'])
      setExpertToDelete(null)
    }
  })

  const handleEdit = (expert) => {
    setEditingExpert({
      ...expert,
      domaines: expert.domaines?.join(', ') || ''
    })
    setIsModalOpen(true)
  }

  const handleView = (expert) => {
    setViewingExpert(expert)
    setIsViewModalOpen(true)
  }

  const handleCreate = () => {
    setEditingExpert({
      nom: '',
      prenom: '',
      titre: '',
      biographie: '',
      photo: '',
      domaines: '',
      is_active: true
    })
    setIsModalOpen(true)
  }

  const handleFileChange = async (e) => {
    const file = e.target.files[0]
    if (!file) return

    setIsUploading(true)
    const formData = new FormData()
    formData.append('file', file)

    try {
      const res = await api.post('/medias/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
      setEditingExpert({ ...editingExpert, photo: res.data.chemin })
    } catch (error) {
      console.error("Upload failed", error)
      alert("Échec de l'upload de l'image")
    } finally {
      setIsUploading(false)
    }
  }

  const filteredExperts = data?.filter(expert => 
    `${expert.prenom} ${expert.nom}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
    expert.titre.toLowerCase().includes(searchTerm.toLowerCase())
  )

  if (isLoading) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-cidh-gold" size={48} /></div>

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h3 className="text-2xl font-bold text-cidh-blue font-montserrat">Gestion des Experts</h3>
          <p className="text-gray-500">Gérez la liste de vos experts et consultants</p>
        </div>
        <button 
          onClick={handleCreate}
          className="btn-primary flex items-center justify-center gap-2 py-3 px-6 rounded-xl shadow-lg shadow-cidh-gold/20 transition-all hover:scale-105 active:scale-95"
        >
          <Plus size={20} /> Nouveau Expert
        </button>
      </div>

      <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-100 flex flex-col md:flex-row gap-4">
        <div className="relative flex-grow">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
          <input 
            type="text" 
            placeholder="Rechercher par nom ou titre..." 
            className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-cidh-gold/20 focus:border-cidh-gold outline-none transition-all"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-gray-50 text-gray-500 text-xs uppercase font-bold">
              <tr>
                <th className="px-6 py-4">Expert</th>
                <th className="px-6 py-4">Titre / Poste</th>
                <th className="px-6 py-4">Domaines</th>
                <th className="px-6 py-4">Statut</th>
                <th className="px-6 py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredExperts?.map((expert) => (
                <tr key={expert.id} className="hover:bg-gray-50/50 transition-colors group">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full overflow-hidden border border-gray-100 flex-shrink-0">
                        <img 
                          src={expert.photo ? (expert.photo.startsWith('http') ? expert.photo : `/media/${expert.photo}`) : `https://ui-avatars.com/api/?name=${expert.prenom}+${expert.nom}&background=random`} 
                          className="w-full h-full object-cover"
                          alt={`${expert.prenom} ${expert.nom}`}
                        />
                      </div>
                      <div className="max-w-[300px]">
                        <div className="font-bold text-cidh-blue text-sm line-clamp-1">{expert.prenom} {expert.nom}</div>
                        {/* Expert ID hidden as requested */}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-gray-700">{expert.titre}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-wrap gap-1">
                      {expert.domaines?.map((d, i) => (
                        <span key={i} className="px-2 py-0.5 bg-gray-100 text-gray-600 text-[10px] rounded-md">{d}</span>
                      ))}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase ${
                      expert.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                    }`}>
                      {expert.is_active ? 'Actif' : 'Inactif'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button onClick={() => handleView(expert)} className="p-2 text-blue-500 hover:bg-blue-50 rounded-lg transition-colors" title="Voir détails">
                        <Eye size={18} />
                      </button>
                      <button onClick={() => handleEdit(expert)} className="p-2 text-cidh-gold hover:bg-gold-50 rounded-lg transition-colors" title="Modifier">
                        <Edit size={18} />
                      </button>
                      <button 
                        onClick={() => setExpertToDelete(expert)}
                        className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                        title="Supprimer"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit Modal Form */}
      {isModalOpen && editingExpert && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-3xl w-full max-w-2xl max-h-[90vh] flex flex-col shadow-2xl animate-scale-in overflow-hidden">
            <div className="p-6 border-b flex justify-between items-center bg-white sticky top-0 z-10">
              <h4 className="text-xl font-bold text-cidh-blue font-montserrat">
                {editingExpert.id ? 'Modifier l\'Expert' : 'Nouvel Expert'}
              </h4>
              <button onClick={() => { setIsModalOpen(false); setEditingExpert(null); }} className="p-2 hover:bg-gray-100 rounded-full"><X size={24} /></button>
            </div>
            
            <form id="expert-form" className="flex-grow overflow-y-auto p-8" onSubmit={(e) => {
              e.preventDefault()
              saveMutation.mutate(editingExpert)
            }}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 animate-fade-in">
                <div className="space-y-2">
                  <label className="text-xs font-bold text-gray-500 uppercase">Prénom *</label>
                  <input type="text" required className="input-field" value={editingExpert.prenom} onChange={(e) => setEditingExpert({...editingExpert, prenom: e.target.value})} />
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-bold text-gray-500 uppercase">Nom *</label>
                  <input type="text" required className="input-field" value={editingExpert.nom} onChange={(e) => setEditingExpert({...editingExpert, nom: e.target.value})} />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <label className="text-xs font-bold text-gray-500 uppercase">Titre / Poste *</label>
                  <input type="text" required className="input-field" value={editingExpert.titre} onChange={(e) => setEditingExpert({...editingExpert, titre: e.target.value})} />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <label className="text-xs font-bold text-gray-500 uppercase">Biographie</label>
                  <textarea rows="4" className="input-field resize-none" value={editingExpert.biographie} onChange={(e) => setEditingExpert({...editingExpert, biographie: e.target.value})}></textarea>
                </div>
                <div className="space-y-2 md:col-span-2">
                  <label className="text-xs font-bold text-gray-500 uppercase">Domaines d'expertise (séparés par des virgules)</label>
                  <input type="text" className="input-field" value={editingExpert.domaines} onChange={(e) => setEditingExpert({...editingExpert, domaines: e.target.value})} placeholder="Agriculture, Environnement, ..."/>
                </div>
                <div className="space-y-2 md:col-span-2">
                  <label className="text-xs font-bold text-gray-500 uppercase">Photo de l'expert</label>
                  <div className="flex items-center gap-4">
                    {editingExpert.photo && (
                      <div className="w-16 h-16 rounded-full overflow-hidden border border-gray-100 flex-shrink-0">
                        <img 
                          src={editingExpert.photo.startsWith('http') ? editingExpert.photo : `/media/${editingExpert.photo}`} 
                          className="w-full h-full object-cover" 
                          alt="Prévisualisation"
                        />
                      </div>
                    )}
                    <div className="flex-grow">
                      <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-200 rounded-2xl cursor-pointer hover:bg-gray-50 hover:border-cidh-gold/50 transition-all group">
                        <div className="flex flex-col items-center justify-center pt-5 pb-6">
                          {isUploading ? (
                            <Loader2 className="animate-spin text-cidh-gold mb-2" size={24} />
                          ) : (
                            <Upload className="text-gray-400 group-hover:text-cidh-gold mb-2" size={24} />
                          )}
                          <p className="text-xs text-gray-500 group-hover:text-cidh-gold"><span className="font-bold">Déposez un fichier</span> ou cliquez</p>
                          <p className="text-[10px] text-gray-400 mt-1">PNG, JPG (Max. 2MB)</p>
                        </div>
                        <input type="file" className="hidden" accept="image/*" onChange={handleFileChange} />
                      </label>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <input type="checkbox" id="is_active" checked={editingExpert.is_active} onChange={(e) => setEditingExpert({...editingExpert, is_active: e.target.checked})} className="w-5 h-5 accent-cidh-gold" />
                  <label htmlFor="is_active" className="text-sm font-medium text-gray-700">Expert actif</label>
                </div>
              </div>
            </form>

            <div className="p-6 border-t flex justify-end gap-4 bg-white sticky bottom-0 z-10">
              <button type="button" onClick={() => { setIsModalOpen(false); setEditingExpert(null); }} className="px-6 py-3 border border-gray-200 rounded-xl font-bold text-gray-400 hover:bg-gray-50 transition-all">Annuler</button>
              <button 
                form="expert-form"
                type="submit"
                disabled={saveMutation.isPending || isUploading}
                className="btn-primary flex items-center gap-2 py-3 px-10 rounded-xl shadow-lg shadow-cidh-gold/20 transition-all active:scale-95 disabled:opacity-50"
              >
                {saveMutation.isPending ? <Loader2 className="animate-spin" size={20} /> : <Check size={20} />}
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Details Modal */}
      {isViewModalOpen && viewingExpert && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-3xl w-full max-w-2xl max-h-[90vh] flex flex-col shadow-2xl animate-scale-in overflow-hidden text-cidh-blue">
            <div className="p-6 border-b flex justify-between items-center bg-white sticky top-0 z-10">
              <h4 className="text-xl font-bold font-montserrat">Détails de l'Expert</h4>
              <button onClick={() => { setIsViewModalOpen(false); setViewingExpert(null); }} className="p-2 hover:bg-gray-100 rounded-full"><X size={24} /></button>
            </div>
            
            <div className="flex-grow overflow-y-auto p-8">
              <div className="flex flex-col items-center mb-8 text-center">
                <div className="w-32 h-32 rounded-full overflow-hidden border-4 border-cidh-gold/20 shadow-xl mb-4">
                  <img 
                    src={viewingExpert.photo ? (viewingExpert.photo.startsWith('http') ? viewingExpert.photo : `/media/${viewingExpert.photo}`) : `https://ui-avatars.com/api/?name=${viewingExpert.prenom}+${viewingExpert.nom}&background=random`} 
                    className="w-full h-full object-cover"
                    alt={`${viewingExpert.prenom} ${viewingExpert.nom}`}
                  />
                </div>
                <h3 className="text-2xl font-bold font-montserrat">{viewingExpert.prenom} {viewingExpert.nom}</h3>
                <p className="text-cidh-gold font-medium">{viewingExpert.titre}</p>
                <div className="mt-4 flex flex-wrap justify-center gap-2">
                  {(Array.isArray(viewingExpert.domaines) ? viewingExpert.domaines : []).map((d, i) => (
                    <span key={i} className="px-3 py-1 bg-cidh-blue/5 text-cidh-blue text-xs font-bold rounded-full border border-cidh-blue/10">{d}</span>
                  ))}
                </div>
              </div>

              <div className="space-y-6">
                <div>
                  <h5 className="text-sm font-bold text-gray-400 uppercase tracking-widest mb-2 flex items-center gap-2">
                    <Info size={16} className="text-cidh-gold" /> Biographie
                  </h5>
                  <div className="bg-gray-50 p-6 rounded-2xl text-gray-700 leading-relaxed italic whitespace-pre-line border border-gray-100">
                    {viewingExpert.biographie || "Aucune biographie disponible."}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                    <div className="bg-gray-50 p-4 rounded-xl border border-gray-100">
                        <p className="text-[10px] font-bold text-gray-400 uppercase mb-1">Status</p>
                        <span className={`text-sm font-bold ${viewingExpert.is_active ? 'text-green-600' : 'text-red-600'}`}>
                            {viewingExpert.is_active ? 'Compte Actif' : 'Compte Inactif'}
                        </span>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-xl border border-gray-100">
                        <p className="text-[10px] font-bold text-gray-400 uppercase mb-1">ID Interne</p>
                        <span className="text-sm font-bold text-gray-700">-</span>
                    </div>
                </div>
              </div>
            </div>

            <div className="p-6 border-t flex justify-end bg-white sticky bottom-0 z-10">
              <button 
                onClick={() => { setIsViewModalOpen(false); setViewingExpert(null); }} 
                className="px-10 py-3 bg-cidh-blue text-white rounded-xl font-bold hover:bg-cidh-blue/90 transition-all shadow-lg active:scale-95"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {expertToDelete && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-white rounded-3xl w-full max-w-md p-8 shadow-2xl animate-scale-in text-center">
            <div className="w-20 h-20 bg-red-50 text-red-500 rounded-full flex items-center justify-center mx-auto mb-6">
              <Trash2 size={40} />
            </div>
            <h4 className="text-2xl font-bold text-cidh-blue mb-2 font-montserrat">Supprimer l'expert ?</h4>
            <p className="text-gray-500 mb-8">
              Êtes-vous sûr de vouloir supprimer <strong>{expertToDelete.prenom} {expertToDelete.nom}</strong> ? Cette action est irréversible.
            </p>
            <div className="flex gap-4">
              <button 
                onClick={() => setExpertToDelete(null)}
                className="flex-grow py-3 border border-gray-200 rounded-xl font-bold text-gray-400 hover:bg-gray-50 transition-all"
              >
                Annuler
              </button>
              <button 
                onClick={() => deleteMutation.mutate(expertToDelete.id)}
                disabled={deleteMutation.isPending}
                className="flex-grow py-3 bg-red-500 text-white rounded-xl font-bold hover:bg-red-600 transition-all shadow-lg shadow-red-500/20 active:scale-95 disabled:opacity-50"
              >
                {deleteMutation.isPending ? <Loader2 className="animate-spin mx-auto" size={20} /> : "Supprimer"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Internal CSS for consistent fields */}
      <style>{`
        .input-field {
          width: 100%;
          padding: 0.75rem 1rem;
          border: 1px solid #E5E7EB;
          border-radius: 0.75rem;
          background-color: white;
          transition: all 0.2s;
          font-size: 0.875rem;
        }
        .input-field:focus {
          outline: none;
          border-color: #D4AF37;
          box-shadow: 0 0 0 3px rgba(212, 175, 55, 0.1);
        }
      `}</style>
    </div>
  )
}

export default AdminExperts
